import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';

/**
 * Interní poznámka dokladu pod zámkem. Text vrací server jen po ověření PINu firmy;
 * bez PINu se dá zjistit jen to, jestli firma PIN má a jestli doklad poznámku má.
 */

export interface PrivateNoteStatus {
  pinSet: boolean;
  exists: boolean;
  updatedAt: string | null;
}

export interface PrivateNote {
  text: string;
  updatedAt: string | null;
}

/**
 * Odemčený PIN si appka drží 10 minut, jen v paměti (ne v úložišti prohlížeče), ať se
 * při procházení faktur nemusí zadávat u každé znovu. Obnovení stránky nebo „Zamknout“
 * ho zapomene.
 */
const PIN_TTL_MS = 10 * 60_000;
const pins = new Map<string, { pin: string; until: number }>();

export function rememberedPin(companyId: string): string | null {
  const hit = pins.get(companyId);
  if (!hit || hit.until < Date.now()) {
    pins.delete(companyId);
    return null;
  }
  return hit.pin;
}

export function rememberPin(companyId: string, pin: string) {
  pins.set(companyId, { pin, until: Date.now() + PIN_TTL_MS });
}

export function forgetPin(companyId: string) {
  pins.delete(companyId);
}

const statusKey = (documentId: string) => ['private-note', documentId];

export function usePrivateNoteStatus(documentId: string) {
  return useQuery({
    queryKey: statusKey(documentId),
    queryFn: () => api.get<PrivateNoteStatus>(`/api/documents/${documentId}/private-note`),
    enabled: !!documentId,
  });
}

export function useOpenPrivateNote(documentId: string) {
  return useMutation({
    mutationFn: (pin: string) =>
      api.post<PrivateNote>(`/api/documents/${documentId}/private-note/open`, { pin }),
  });
}

export function useSavePrivateNote(documentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { pin: string; text: string }) =>
      api.put<PrivateNote>(`/api/documents/${documentId}/private-note`, v),
    onSuccess: () => qc.invalidateQueries({ queryKey: statusKey(documentId) }),
  });
}

export function useSetNotePin(companyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { pin: string; currentPin?: string }) =>
      api.put<void>(`/api/companies/${companyId}/note-pin`, v),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['private-note'] }),
  });
}
